import { Worksheet } from '../budgetCalculatorModel'
import * as calculations from './calculations'
import {
  CompanyFormData,
  SeasonFormData,
  OwnerFormData,
  FieldEmployee,
  FieldEmployeesFormData,
  AdminEmployeesFormData,
  TrucksFormData,
  TrailersFormData,
  HeavyMachineryFormData
} from './types'
import { createDefaultFieldEmployee } from './constants'
import { Dayjs } from 'dayjs'

type CrewHoursTarget = {
  seasonData: SeasonFormData
  fieldEmployeesData: FieldEmployeesFormData
}

export const withCrewHoursPerWeek = <T extends CrewHoursTarget>(target: T, hoursPerWeek: number): T => ({
  ...target,
  seasonData: { ...target.seasonData, hoursPerWeek },
  fieldEmployeesData: {
    ...target.fieldEmployeesData,
    employees: target.fieldEmployeesData.employees.map((employee) => ({ ...employee, hoursPerWeek }))
  }
})

export const growFieldRoster = (
  employees: FieldEmployee[],
  count: number,
  seasonHoursPerWeek: number | null
): FieldEmployee[] => {
  if (count <= employees.length) {
    return employees.slice(0, count)
  }
  const template = employees[0] ?? createDefaultFieldEmployee(0, seasonHoursPerWeek)
  return Array.from({ length: count }, (_, index) => {
    return employees[index] ?? { ...template, name: `Employee ${index + 1}` }
  })
}

/**
 * Calculate the number of working weeks between two dates
 * Re-exported from legacy calculations for backwards compatibility with tests
 */
export const calculateWorkingWeeks = (startDate: Dayjs, endDate: Dayjs): number => {
  const weeks = endDate.diff(startDate, 'week')
  return Math.max(1, weeks) // At least 1 week
}

/**
 * Calculate total hours worked per year based on hours per week and working weeks
 * Re-exported from legacy calculations for backwards compatibility with tests
 */
export { calculateHoursWorkedPerYear } from './employeeCostModel'

/**
 * Convert Budget Builder data into a Worksheet
 */
export const createWorksheetFromBudgetBuilder = (
  companyData: CompanyFormData,
  seasonData: SeasonFormData,
  ownerData: OwnerFormData,
  fieldEmployeesData: FieldEmployeesFormData,
  adminEmployeesData: AdminEmployeesFormData,
  trucksData: TrucksFormData,
  trailersData: TrailersFormData,
  heavyMachineryData: HeavyMachineryFormData
): Worksheet => {
  return calculations.buildWorksheet(
    companyData,
    seasonData,
    ownerData,
    fieldEmployeesData,
    adminEmployeesData,
    trucksData,
    trailersData,
    heavyMachineryData
  )
}

/**
 * Validation functions for each step
 */
export const isStep1Valid = (data: CompanyFormData): boolean => {
  return data.salesGoal !== null &&
         data.profitMargin !== null &&
         data.revenueSource !== null
}

export const isStep2Valid = (data: SeasonFormData): boolean => {
  return data.seasonStartDate !== null &&
         data.seasonEndDate !== null &&
         data.daysPerWeek !== null &&
         data.hoursPerWeek !== null
}

export const isStep3Valid = (data: OwnerFormData): boolean => {
  return data.fieldWorkPercentage !== null &&
         data.hoursPerWeek !== null &&
         data.annualSalary !== null
}

export const isStep4Valid = (data: FieldEmployeesFormData): boolean => {
  // Employee count is always valid (defaults to 0, can be 0 or higher)
  // All field employees must have all required fields filled based on payType
  return data.employees.every(emp => {
    if (emp.name.trim() === '') return false
    if (emp.payType === 'Hourly') {
      return emp.hourlyRate !== null && emp.hoursPerWeek !== null
    } else {
      return emp.annualSalary !== null
    }
  })
}

export const isStep5Valid = (data: AdminEmployeesFormData): boolean => {
  // Employee count is always valid (defaults to 0, can be 0 or higher)
  // All admin employees must have all required fields filled based on payType
  return data.employees.every(emp => {
    if (emp.name.trim() === '') return false
    if (emp.payType === 'Salary') {
      return emp.annualSalary !== null
    } else {
      return emp.hourlyRate !== null && emp.hoursPerWeek !== null
    }
  })
}

export const isStep6Valid = (data: TrucksFormData): boolean => {
  return data.trucks.every(truck => {
    if (truck.name.trim() === '') return false
    if (truck.ownershipType === 'Own') {
      return truck.replacementCost !== null &&
             truck.avgUsefulLife !== null &&
             truck.sellPrice !== null
    } else {
      return truck.monthlyPayment !== null
    }
  })
}

export const isStep7Valid = (data: TrailersFormData): boolean => {
  return data.trailers.every(trailer => {
    if (trailer.name.trim() === '') return false
    if (trailer.ownershipType === 'Own') {
      return trailer.replacementCost !== null &&
             trailer.avgUsefulLife !== null &&
             trailer.sellPrice !== null
    } else {
      return trailer.monthlyPayment !== null
    }
  })
}

export const isStep8Valid = (data: HeavyMachineryFormData): boolean => {
  return data.machinery.every(machinery => {
    if (machinery.equipmentName.trim() === '') return false
    if (machinery.ownershipType === 'Own') {
      return machinery.replacementCost !== null &&
             machinery.avgUsefulLife !== null &&
             machinery.sellPrice !== null
    } else {
      return machinery.monthlyPayment !== null
    }
  })
}
